import { useCallback, useEffect, useRef, useState } from "react";
import { MarketingProductPicker } from "../components/MarketingProductPicker";
import { Clapperboard, Download, ImagePlus, Loader2, Send, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import VoiceRecorder from "../components/VoiceRecorder";

const STATUS_AR = {
  awaiting_confirmation: "بانتظار تأكيدك", needs_duration_confirmation: "بانتظار تأكيد المدة", queued: "في الانتظار",
  generating: "جارٍ إنشاء الفيديو…", validating: "مراجعة نهائية…", completed: "جاهز", failed: "تعذّر الإنشاء",
  failed_policy: "لم يوافق السياسة", rejected_specialization: "خارج تخصص المتجر", rejected_policy: "مرفوض (سياسة المحتوى)",
};
const BUSY = ["queued", "generating", "validating"];
const fmt = (s) => (s ? new Date(s).toLocaleString("ar-SY", { dateStyle: "medium", timeStyle: "short" }) : "");
const backend = process.env.REACT_APP_BACKEND_URL;

const readAsDataUrl = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });

export default function MarketingVideos() {
  const [st, setSt] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [unavailable, setUnavailable] = useState({});
  const [mediaTok, setMediaTok] = useState({});
  const mediaTokRef = useRef({});
  const [text, setText] = useState("");
  const [picked, setPicked] = useState([]);
  const [images, setImages] = useState([]);
  const [pending, setPending] = useState(null); // job awaiting confirmation
  const [busy, setBusy] = useState(false);
  const clientKey = useRef(null);

  const load = useCallback(async () => {
    try {
      const [s, j] = await Promise.all([api.get("/marketing-videos/status"), api.get("/marketing-videos/jobs")]);
      setSt(s.data); setJobs(j.data);
      const done = j.data.filter((x) => x.has_video && !mediaTokRef.current[x.id]);
      if (done.length) {
        const pairs = await Promise.all(done.map((x) => api.get(`/marketing-videos/jobs/${x.id}/media-token`).then((r) => [x.id, r.data.token]).catch(() => null)));
        mediaTokRef.current = { ...mediaTokRef.current, ...Object.fromEntries(pairs.filter(Boolean)) };
        setMediaTok(mediaTokRef.current);
      }
      setPending(j.data.find((x) => ["awaiting_confirmation", "needs_duration_confirmation"].includes(x.status)) || null);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر تحميل الصفحة"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!jobs.some((j) => BUSY.includes(j.status))) return undefined;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [jobs, load]);

  const addImages = async (files) => {
    const list = [...files].slice(0, 3 - images.length);
    for (const f of list) {
      if (f.size > 4 * 1024 * 1024) { toast.error("حجم الصورة كبير (الحد 4MB)"); continue; }
      setImages((p) => [...p, { name: f.name, data: null }]);
      const data = await readAsDataUrl(f);
      setImages((p) => p.map((x) => (x.name === f.name && !x.data ? { ...x, data } : x)));
    }
  };

  const submit = async () => {
    if (busy || pending) return;
    if (!text.trim() && !picked.length && !images.length) { toast.error("اكتب أو سجّل ما تريده في الفيديو"); return; }
    setBusy(true);
    clientKey.current = clientKey.current || crypto.randomUUID();
    try {
      const r = await api.post("/marketing-videos/jobs/plan", { text, product_ids: picked, images_b64: images.map((i) => i.data).filter(Boolean), client_key: clientKey.current });
      const j = r.data;
      if (j.status.startsWith("rejected")) toast.error(j.reject_reason_ar || "تم رفض الطلب"); else setPending(j);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر فهم الطلب"); } finally { setBusy(false); }
  };

  const confirm = async () => {
    if (busy || !pending) return;
    setBusy(true);
    try {
      await api.post(`/marketing-videos/jobs/${pending.id}/confirm`, { accept_max_duration: pending.status === "needs_duration_confirmation" });
      toast.success("بدأ إنشاء الفيديو، سنُعلمك عند اكتماله");
      setText(""); setPicked([]); setImages([]); setPending(null); clientKey.current = null;
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التأكيد"); } finally { setBusy(false); }
  };

  const discard = () => { setPending(null); clientKey.current = null; };

  if (!st) return <div className="p-6 text-center text-[#666666]" data-testid="mv-loading">جارٍ التحميل…</div>;

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="marketing-videos-page">
      <div className="flex flex-col items-start gap-2 md:flex-row md:items-center md:justify-between md:gap-3">
        <h1 className="text-2xl md:text-3xl font-bold text-[#09637E] flex items-center gap-2"><Clapperboard className="w-7 h-7 shrink-0" /> صناعة الفيديوهات التسويقية</h1>
        <span className="text-xs md:text-sm font-bold px-3 py-1.5 rounded-full bg-[#09637E]/10 text-[#09637E] md:whitespace-nowrap" data-testid="mv-quota">متبقي {st.remaining} من {st.limit} فيديوهات هذا الشهر</span>
      </div>

      {!st.enabled ? (
        <div className="bg-[#FFF7E6] border border-[#F5C26B] rounded-xl p-4 text-[#7A4B00] text-sm md:text-base" data-testid="mv-no-products">
          يجب رفع منتج واحد على الأقل إلى إمداد قبل استخدام صناعة الفيديوهات التسويقية.
        </div>
      ) : (
        <div className="bg-white border border-[#7AB2B2] rounded-2xl p-3 md:p-4 space-y-3" data-testid="mv-composer">
          <MarketingProductPicker products={st.products} picked={picked} onChange={setPicked} disabled={!!pending} max={5} />
          <div className="flex flex-wrap items-center gap-2">
            <label className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-[#7AB2B2] cursor-pointer ${pending || images.length >= 3 ? "opacity-50 pointer-events-none" : ""}`}>
              <ImagePlus className="w-4 h-4" /> صور من هاتفك
              <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" data-testid="mv-upload-input" onChange={(e) => addImages(e.target.files)} />
            </label>
            {images.map((im, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-xs bg-[#EBF4F6] px-2 py-1 rounded-full" data-testid={`mv-image-${i}`}>
                {im.data ? <img src={im.data} alt="" className="w-6 h-6 rounded object-cover" /> : <Loader2 className="w-3 h-3 animate-spin" />}
                {im.name.slice(0, 14)}
                <button type="button" onClick={() => setImages((p) => p.filter((_, k) => k !== i))} aria-label="إزالة"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
          <div className="flex items-end gap-2">
            <textarea value={text} onChange={(e) => setText(e.target.value)} disabled={!!pending} rows={2} dir="auto" data-testid="mv-text"
              placeholder="مثال: فيديو قصير يعرض بلاط الأرضيات الرمادي في صالة عصرية…"
              className="flex-1 border border-[#7AB2B2] rounded-xl p-3 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-[#09637E]/40 resize-none" />
            <VoiceRecorder endpoint="/marketing-videos/transcribe" disabled={!!pending || busy} onTranscript={(t) => setText((v) => (v ? v + " " : "") + t)} />
            <button type="button" onClick={submit} disabled={busy || !!pending} data-testid="mv-submit"
              className="h-11 w-11 rounded-full bg-[#09637E] text-white flex items-center justify-center disabled:opacity-50">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </button>
          </div>
        </div>
      )}

      {pending && (
        <div className="bg-[#EBF4F6] border border-[#09637E]/30 rounded-2xl p-4 space-y-2" data-testid="mv-summary">
          <div className="font-bold text-[#09637E] flex items-center gap-2"><Sparkles className="w-5 h-5" /> هكذا فهمنا طلبك</div>
          {pending.product_names?.length > 0 && <div className="text-sm">المنتجات: {pending.product_names.join("، ")}</div>}
          {pending.image_count > 0 && <div className="text-sm">صور مرفقة: {pending.image_count}</div>}
          <div className="text-sm md:text-base whitespace-pre-wrap" data-testid="mv-concept">{pending.concept_ar}</div>
          <div className="text-sm">المدة المخططة: <b data-testid="mv-planned">{pending.planned_seconds} ثانية</b></div>
          {pending.question_ar && <div className="text-sm font-bold text-[#7A4B00]" data-testid="mv-question">{pending.question_ar}</div>}
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={confirm} disabled={busy} data-testid="mv-confirm" className="flex-1 h-11 rounded-full bg-[#09637E] text-white font-bold disabled:opacity-50">
              {pending.question_ar ? "نعم، إنشاء الفيديو بمدة 30 ثانية" : "إنشاء الفيديو"}
            </button>
            <button type="button" onClick={discard} disabled={busy} data-testid="mv-discard" className="h-11 px-4 rounded-full border border-[#7AB2B2] text-[#333333]">تعديل</button>
          </div>
        </div>
      )}

      <div className="space-y-2" data-testid="mv-history">
        <h2 className="text-base md:text-lg font-bold text-[#333333]">فيديوهاتي</h2>
        {jobs.filter((j) => !["awaiting_confirmation", "needs_duration_confirmation"].includes(j.status)).length === 0 && <div className="text-sm text-[#666666]">لا توجد فيديوهات بعد</div>}
        {jobs.filter((j) => !["awaiting_confirmation", "needs_duration_confirmation"].includes(j.status)).map((j) => (
          <div key={j.id} className="bg-white border border-[#7AB2B2] rounded-xl p-3 space-y-2" data-testid={`mv-job-${j.id}`}>
            <div className="flex items-center justify-between gap-2 text-xs text-[#666666]">
              <span>{fmt(j.created_at)}</span>
              <span className={`font-bold px-2 py-0.5 rounded-full ${j.status === "completed" ? "bg-green-50 text-green-700" : BUSY.includes(j.status) ? "bg-[#EBF4F6] text-[#09637E]" : "bg-red-50 text-red-700"}`} data-testid={`mv-status-${j.id}`}>
                {BUSY.includes(j.status) && <Loader2 className="inline w-3 h-3 animate-spin ml-1" />}{STATUS_AR[j.status] || j.status}
              </span>
            </div>
            <div className="text-sm text-[#333333] line-clamp-2">{j.concept_ar || j.request_text}</div>
            <div className="text-xs text-[#666666]">{j.planned_seconds ? `${j.planned_seconds} ثانية` : ""}{j.product_names?.length ? ` · ${j.product_names.join("، ")}` : ""}</div>
            {(j.error_ar || j.reject_reason_ar) && <div className="text-xs text-red-700">{j.error_ar || j.reject_reason_ar}</div>}
            {j.has_video && (
              <div className="space-y-2">
                {mediaTok[j.id] ? (
                  <video controls playsInline preload="metadata" className="w-full max-h-[70vh] rounded-lg bg-black" src={`${backend}/api/marketing-videos/jobs/${j.id}/video?t=${mediaTok[j.id]}`} data-testid={`mv-video-${j.id}`}
                    onError={() => setUnavailable((s) => ({ ...s, [j.id]: true }))} />
                ) : (
                  <div className="w-full aspect-video rounded-lg bg-black/80 flex items-center justify-center text-white/70 text-sm" data-testid={`mv-video-loading-${j.id}`}>جارٍ تجهيز المشغّل…</div>
                )}
                {unavailable[j.id] && <div className="text-xs text-amber-700" data-testid={`mv-video-unavailable-${j.id}`}>الفيديو غير متاح مؤقتاً، يرجى المحاولة لاحقاً.</div>}
                <a href={`${backend}/api/marketing-videos/jobs/${j.id}/video?t=${mediaTok[j.id] || ""}`} download className="inline-flex items-center gap-1 text-sm font-bold text-[#09637E]" data-testid={`mv-download-${j.id}`}><Download className="w-4 h-4" /> تحميل الفيديو</a>
                {j.storage_durable === false && <div className="text-xs text-amber-700" data-testid={`mv-storage-warning-${j.id}`}>هذا الفيديو محفوظ مؤقتاً — نزّله الآن للاحتفاظ به.</div>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
